package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.requests.SignEventAttendeeRequest
import com.rainbowclubs.rainbow.domain.use_cases.events.SignEventAttendeeUseCase
import com.rainbowclubs.rainbow.domain.use_cases.user.GetUserUseCase
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class TakeAttendanceViewModel(
    private val clubId: String,
    private val eventId: String?,
    private val signEventAttendeeUseCase: SignEventAttendeeUseCase,
    getUserUseCase: GetUserUseCase,
) : ViewModel() {

    sealed class NavigationEvent {
        data object Back : NavigationEvent()
    }

    private val adminUserId = getUserUseCase()?.id.orEmpty()

    private val _uiState = MutableStateFlow(
        TakeAttendanceUiState(
            clubId = clubId,
            eventId = eventId,
            status = if (eventId.isNullOrBlank()) {
                TakeAttendanceStatus.Failure(
                    reason = "No live event is available for attendance.",
                    canRetry = false,
                )
            } else {
                TakeAttendanceStatus.Ready
            },
        )
    )
    val uiState: StateFlow<TakeAttendanceUiState> = _uiState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    fun navigateBack() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    fun onQrCodeScanned(qrCode: String) {
        val currentStatus = _uiState.value.status
        if (currentStatus is TakeAttendanceStatus.Signing) return

        val attendeeUserId = qrCode.trim()
        if (attendeeUserId.isBlank()) {
            _uiState.value = _uiState.value.copy(
                status = TakeAttendanceStatus.Failure("Invalid attendee QR code")
            )
            return
        }

        val liveEventId = eventId
        if (liveEventId.isNullOrBlank()) {
            _uiState.value = _uiState.value.copy(
                status = TakeAttendanceStatus.Failure(
                    reason = "No live event is available for attendance.",
                    canRetry = false,
                )
            )
            return
        }

        if (adminUserId.isBlank()) {
            _uiState.value = _uiState.value.copy(
                status = TakeAttendanceStatus.Failure("Admin user is not available.")
            )
            return
        }

        _uiState.value = _uiState.value.copy(status = TakeAttendanceStatus.Signing)

        viewModelScope.launch {
            signEventAttendeeUseCase(
                eventId = liveEventId,
                request = SignEventAttendeeRequest(
                    adminUserId = adminUserId,
                    attendeeUserId = attendeeUserId,
                ),
            ).onSuccess {
                _uiState.value = _uiState.value.copy(status = TakeAttendanceStatus.Success)
            }.onFailure { error ->
                _uiState.value = _uiState.value.copy(
                    status = TakeAttendanceStatus.Failure(
                        error.message?.takeIf { it.isNotBlank() } ?: "Could not sign attendance."
                    )
                )
            }
        }
    }

    fun resetScanner() {
        _uiState.value = _uiState.value.copy(status = TakeAttendanceStatus.Ready)
    }
}

data class TakeAttendanceUiState(
    val clubId: String,
    val eventId: String?,
    val status: TakeAttendanceStatus,
)

sealed class TakeAttendanceStatus {
    data object Ready : TakeAttendanceStatus()
    data object Signing : TakeAttendanceStatus()
    data object Success : TakeAttendanceStatus()
    data class Failure(
        val reason: String,
        val canRetry: Boolean = true,
    ) : TakeAttendanceStatus()
}
